import React, { createContext, useState, useEffect, ReactNode } from 'react';
import client from '../api/client';

export type PortalSection = 'meetings' | 'bestPractices' | 'aiTool' | 'regulatoryUpdates';

export interface User {
  userId: string;
  email: string;
  name: string;
  isAdmin: boolean;
  status: 'pending' | 'approved' | 'rejected';
  role: 'super_admin' | 'admin' | 'user';
  firmId: string;
  agentAccess?: boolean;
  sectionAccess?: Partial<Record<PortalSection, boolean>>;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAdmin: boolean;
  login: () => void;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}


export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize auth by checking if token exists in localStorage
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check if we have a stored token
        const token = localStorage.getItem('rdf_token');
        if (token) {
          // Try to fetch current user - validates the token is still valid
          await fetchCurrentUser();
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Get current user from /auth/me endpoint
  const fetchCurrentUser = async () => {
    try {
      const response = await client.get('/auth/me');
      setUser(response.data as User);
      return true;
    } catch (err) {
      console.error('Failed to fetch current user:', err);
      setUser(null);
      return false;
    }
  };

  // New login function: no longer takes token parameter
  // Token is managed by server via httpOnly cookie
  const login = async () => {
    try {
      // Fetch the current logged-in user from /auth/me
      await fetchCurrentUser();
    } catch (err) {
      console.error('Failed during login setup:', err);
      setUser(null);
    }
  };

  const logout = async () => {
    try {
      await client.post('/auth/logout');
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('rdf_token');
      setUser(null);
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAdmin: user?.isAdmin ?? false,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
